import { createBackendAdapter } from "../../../../packages/app-ui/js/backendAdapter.js";
import { uploadFileWithProgress } from "../../../../packages/app-ui/js/uploadWithProgress.js";

export function createAppBackendAdapter(options = {}) {
  const apiBaseUrl = normalizeBaseUrl(options.apiBaseUrl);
  const fetchImpl = options.fetchImpl || fetch;
  const getAuthToken =
    typeof options.getAuthToken === "function" ? options.getAuthToken : null;
  const credentialsMode = options.credentials || "include";

  async function requestJson(path, init = {}) {
    const headers = new Headers(init.headers || {});
    if (
      init.body &&
      !(init.body instanceof FormData) &&
      !headers.has("content-type")
    ) {
      headers.set("content-type", "application/json");
    }
    if (getAuthToken) {
      const token = await getAuthToken();
      if (token && !headers.has("authorization")) {
        headers.set("authorization", `Bearer ${token}`);
      }
    }
    const response = await fetchImpl(`${apiBaseUrl}${path}`, {
      method: init.method || "GET",
      headers,
      body: init.body,
      credentials: credentialsMode,
      signal: init.signal,
    });
    const payload = await safeParseJson(response);
    if (!response.ok) {
      const message = payload?.message || `request failed for ${path}`;
      const error = new Error(message);
      error.status = response.status;
      error.payload = payload;
      throw error;
    }
    return payload;
  }

  return createBackendAdapter({
    createUpload: (payload = {}) =>
      requestJson("/v1/app/uploads", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    uploadToR2: (payload = {}) =>
      uploadFileWithProgress({
        url: payload.upload_url,
        file: payload.file,
        headers: payload.headers,
        method: payload.method || "PUT",
        onProgress: payload.onProgress,
        signal: payload.signal || null,
      }),
    createJob: (payload = {}) =>
      requestJson("/v1/app/transcription_jobs", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    getJob: (jobId) => requestJson(`/v1/app/transcription_jobs/${jobId}`),
    getTranscript: (jobId) =>
      requestJson(`/v1/app/transcription_jobs/${jobId}/transcript`),
    deleteJob: (jobId) =>
      requestJson(`/v1/app/transcription_jobs/${jobId}`, {
        method: "DELETE",
      }),
    getQuota: () => requestJson("/v1/app/quota"),
  });
}

function normalizeBaseUrl(value) {
  if (!value || typeof value !== "string") {
    throw new Error("apiBaseUrl is required for the app backend adapter");
  }
  return value.replace(/\/$/, "");
}

async function safeParseJson(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}
